import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { networkManager, getErrorMessage } from '../api/NetworkManager';
import { useAuth } from '../context/AuthContext';
import { BreakthroughTips } from '../components/BreakthroughTip';
import { getTipsForContext } from '../lib/breakthrough';
import { PersonaTips } from '../components/PersonaTip';
import { PERSONAS } from '../lib/personas';
import {
  ArrowLeft,
  Loader2,
  Check,
  AlertCircle,
  GitBranch,
  Play,
  Upload,
  Lock,
  Send,
  Undo2,
  Sparkles,
  X,
  Target,
} from 'lucide-react';

interface EventTheme {
  id: string;
  name: string;
  description: string;
}

interface EventWithThemes {
  themes: EventTheme[];
  name: string;
}

/** Matches backend RefineResult from ai.service.ts */
interface RefineResult {
  refined: string;
  suggestions: string[];
}

interface Submission {
  submissionId: string;
  title: string;
  problem: string;
  solution: string;
  impact: string;
  technicalApproach: string;
  demoUrl: string;
  repoUrl: string;
  fileUrls: string[];
  status: 'draft' | 'submitted';
}

interface ApiResponse<T> {
  status: boolean;
  data: T;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// Single source of truth for the submission attachment size cap so the label
// and the client-side check can't drift apart. Uploads go direct to S3 via
// presigned PUT (no Lambda/API Gateway in the path), so this limit is
// client-enforced — raise it here to raise it everywhere.
const MAX_ATTACHMENT_MB = 50;
const MAX_ATTACHMENT_BYTES = MAX_ATTACHMENT_MB * 1024 * 1024;

const SECTIONS = [
  { key: 'title' as const, label: 'Project Title', type: 'input', maxLength: 120, hint: 'A clear, memorable name for your idea' },
  { key: 'problem' as const, label: 'Problem Statement', type: 'textarea', maxLength: 5000, hint: 'What pain point are you solving? Who feels it? How big is it?' },
  { key: 'solution' as const, label: 'Solution', type: 'textarea', maxLength: 5000, hint: 'What did you build? How does it work? What makes it different?' },
  { key: 'impact' as const, label: 'Impact', type: 'textarea', maxLength: 5000, hint: 'What measurable outcomes does this drive? Who benefits and how?' },
  { key: 'technicalApproach' as const, label: 'Technical Approach', type: 'textarea', maxLength: 5000, hint: 'Architecture, AI usage, key tech decisions, and feasibility' },
];

type FieldKey = 'title' | 'problem' | 'solution' | 'impact' | 'technicalApproach' | 'demoUrl' | 'repoUrl';

export function SubmissionEditorPage() {
  const { eventId, teamId } = useParams<{ eventId: string; teamId: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canRefine = can('ai:refine');


  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [now, setNow] = useState(Date.now());

  // AI state
  const [refiningSection, setRefiningSection] = useState<string | null>(null);
  const [refineResult, setRefineResult] = useState<{ section: string; data: RefineResult } | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  // Theme state — read from the team, persisted via PATCH team/theme.
  // Intentionally separate from submission progress (a missing theme never
  // blocks 100%).
  const [eventThemes, setEventThemes] = useState<EventTheme[]>([]);
  const [teamThemeId, setTeamThemeId] = useState<string | null>(null);
  const [themeSaveState, setThemeSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const pendingChanges = useRef<Partial<Record<FieldKey, string>>>({});
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSaved = useRef<Record<string, string>>({});

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    async function load() {
      try {
        let sub: Submission;
        try {
          const res = await networkManager.get<ApiResponse<Submission>>(
            `/api/events/${eventId}/teams/${teamId}/submissions`,
          );
          sub = res.data;
        } catch (getErr: unknown) {
          const status = (getErr as { response?: { status?: number } })?.response?.status;
          if (status === 404) {
            const createRes = await networkManager.post<ApiResponse<Submission>>(
              `/api/events/${eventId}/teams/${teamId}/submissions`,
            );
            sub = createRes.data;
          } else {
            throw getErr;
          }
        }
        setSubmission(sub);
        lastSaved.current = {
          title: sub.title || '',
          problem: sub.problem || '',
          solution: sub.solution || '',
          impact: sub.impact || '',
          technicalApproach: sub.technicalApproach || '',
          demoUrl: sub.demoUrl || '',
          repoUrl: sub.repoUrl || '',
        };

        // Pull the team + event themes for the theme picker. Non-fatal: if
        // either fails we just hide the picker rather than blocking the editor.
        try {
          const [teamRes, eventRes] = await Promise.all([
            networkManager.get<ApiResponse<{ themeId: string | null }>>(
              `/api/events/${eventId}/teams/${teamId}`,
            ),
            networkManager.get<ApiResponse<EventWithThemes>>(`/api/events/${eventId}`),
          ]);
          setTeamThemeId(teamRes.data.themeId ?? null);
          setEventThemes(eventRes.data.themes ?? []);
        } catch {
          /* theme picker stays hidden; not a blocker */
        }
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to load submission';
        setError(msg);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [eventId, teamId]);

  const doSave = useCallback(async () => {
    if (!submission) return;
    const changes = { ...pendingChanges.current };
    if (Object.keys(changes).length === 0) return;
    pendingChanges.current = {};

    setSaveStatus('saving');
    try {
      await networkManager.put<ApiResponse<unknown>>(
        `/api/events/${eventId}/teams/${teamId}/submissions/${submission.submissionId}`,
        changes,
      );
      Object.assign(lastSaved.current, changes);
      setSaveStatus('saved');
      setSavedAt(new Date());
    } catch {
      setSaveStatus('error');
      Object.assign(pendingChanges.current, changes);
    }
  }, [submission, eventId, teamId]);

  function handleChange(key: FieldKey, value: string) {
    if (!submission) return;
    setSubmission({ ...submission, [key]: value });

    if (value !== lastSaved.current[key]) {
      pendingChanges.current[key] = value;
    } else {
      delete pendingChanges.current[key];
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(doSave, 2000);
  }

  async function handleSubmit() {
    if (!submission) return;
    setSubmitting(true);
    setError(null);
    try {
      if (Object.keys(pendingChanges.current).length > 0) await doSave();
      await networkManager.post<ApiResponse<unknown>>(
        `/api/events/${eventId}/teams/${teamId}/submissions/${submission.submissionId}/submit`,
      );
      setSubmission({ ...submission, status: 'submitted' });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to finalize';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  // Persist theme changes via PATCH. Empty string maps to null on the wire.
  // Not debounced — theme is a small, discrete choice, so immediate feedback
  // beats the complexity of a deferred save.
  async function handleThemeChange(nextThemeId: string) {
    const normalized = nextThemeId || null;
    // Optimistic UI — revert on failure so the select reflects persisted state.
    const previous = teamThemeId;
    setTeamThemeId(normalized);
    setThemeSaveState('saving');
    try {
      await networkManager.patch<ApiResponse<{ themeId: string | null }>>(
        `/api/events/${eventId}/teams/${teamId}/theme`,
        { themeId: normalized },
      );
      setThemeSaveState('saved');
      setTimeout(() => setThemeSaveState('idle'), 1800);
    } catch (err: unknown) {
      setTeamThemeId(previous);
      setThemeSaveState('error');
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Could not save theme';
      setError(msg);
    }
  }

  async function handleUnsubmit() {
    if (!submission) return;
    try {
      await networkManager.post<ApiResponse<unknown>>(
        `/api/events/${eventId}/teams/${teamId}/submissions/${submission.submissionId}/unsubmit`,
      );
      setSubmission({ ...submission, status: 'draft' });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to unlock';
      setError(msg);
    }
  }

  async function handleRefine(sectionKey: string, content: string) {
    if (!content.trim()) return;
    setRefiningSection(sectionKey);
    setRefineResult(null);
    setAiError(null);
    try {
      const res = await networkManager.post<{ status: boolean; data: RefineResult }>(
        '/api/ai/refine',
        { eventId, teamId, section: sectionKey, content },
      );
      setRefineResult({ section: sectionKey, data: res.data });
    } catch (err) {
      setAiError(getErrorMessage(err, 'AI refinement failed — please try again later'));
    } finally {
      setRefiningSection(null);
    }
  }

  function applyRefinement() {
    if (!refineResult || !submission) return;
    const key = refineResult.section as FieldKey;
    handleChange(key, refineResult.data.refined);
    setRefineResult(null);
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
      </div>
    );
  }

  if (error && !submission) {
    return (
      <div className="animate-fade-in">
        <button onClick={() => navigate(-1)} className="btn-ghost mb-4">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="card border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      </div>
    );
  }

  if (!submission) return null;

  const isLocked = submission.status === 'submitted';
  const filledSections = SECTIONS.filter((s) => (submission[s.key] ?? '').trim().length > 0).length;
  const progress = Math.round((filledSections / SECTIONS.length) * 100);
  const savedAgo = savedAt ? Math.round((now - savedAt.getTime()) / 1000) : null;

  return (
    <div className="animate-fade-in pb-20">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to={`/events/${eventId}/teams/${teamId}`} className="btn-ghost">
          <ArrowLeft className="h-4 w-4" /> Team
        </Link>
        <span className="flex items-center gap-1.5 text-xs text-ink-tertiary">
          {saveStatus === 'saving' && <><Loader2 className="h-3 w-3 animate-spin" /> Saving...</>}
          {saveStatus === 'saved' && savedAgo !== null && <><Check className="h-3 w-3 text-green-600" /> Saved {savedAgo < 5 ? 'just now' : `${savedAgo}s ago`}</>}
          {saveStatus === 'error' && <><AlertCircle className="h-3 w-3 text-red-500" /> Failed — <button onClick={doSave} className="underline">retry</button></>}
          {saveStatus === 'idle' && 'Auto-saves as you type'}
        </span>
      </div>

      {isLocked && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <Lock className="h-4 w-4 shrink-0" />
          <span>Finalized for demo day. <button onClick={handleUnsubmit} className="font-medium underline">Unlock to keep editing</button></span>
        </div>
      )}

      {error && (
        <div className="mt-4 card border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {/* Progress */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-ink-secondary">{filledSections}/{SECTIONS.length} sections</span>
          <span className="font-medium text-ink">{progress}%</span>
        </div>
        <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-surface-sunken">
          <div
            className="h-full rounded-full bg-gradient-to-r from-amber-500 via-red-500 to-purple-600 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Theme picker — optional, does not affect progress. Hidden if the
          event has no themes configured. */}
      {eventThemes.length > 0 && (
        <section className="mt-6 card p-4 sm:p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <label htmlFor="submission-theme" className="flex items-center gap-1.5 text-sm font-semibold text-ink">
                <Target className="h-4 w-4 text-ink-tertiary" />
                Theme <span className="text-ink-tertiary font-normal">(optional)</span>
              </label>
              <p className="mt-0.5 text-xs text-ink-tertiary">
                Which hackathon theme is your project targeting? You can change this any time
                until submissions lock.
              </p>
            </div>
            <span className="text-xs text-ink-tertiary" aria-live="polite">
              {themeSaveState === 'saving' && (
                <>
                  <Loader2 className="inline h-3 w-3 animate-spin" /> Saving…
                </>
              )}
              {themeSaveState === 'saved' && (
                <>
                  <Check className="inline h-3 w-3 text-green-600" /> Saved
                </>
              )}
              {themeSaveState === 'error' && (
                <>
                  <AlertCircle className="inline h-3 w-3 text-red-500" /> Failed
                </>
              )}
            </span>
          </div>
          <select
            id="submission-theme"
            className="input-base mt-3 w-full"
            value={teamThemeId ?? ''}
            onChange={(e) => handleThemeChange(e.target.value)}
            disabled={isLocked || themeSaveState === 'saving'}
          >
            <option value="">No theme yet — I&apos;ll decide later</option>
            {eventThemes.map((theme) => (
              <option key={theme.id} value={theme.id}>
                {theme.name}
              </option>
            ))}
          </select>
          {teamThemeId && eventThemes.find((t) => t.id === teamThemeId)?.description && (
            <p className="mt-2 text-xs text-ink-secondary">
              {eventThemes.find((t) => t.id === teamThemeId)?.description}
            </p>
          )}
        </section>
      )}

      {/* Sections */}
      <div className="mt-6 space-y-5">
        {SECTIONS.map((section) => (
          <section key={section.key} className="card p-4 sm:p-5">
            <label className="text-sm font-semibold text-ink">{section.label}</label>
            <p className="mt-0.5 text-xs text-ink-tertiary">{section.hint}</p>
            {section.type === 'input' ? (
              <input
                type="text"
                className="input-base mt-2"
                value={submission[section.key] ?? ''}
                onChange={(e) => handleChange(section.key, e.target.value)}
                maxLength={section.maxLength}
                disabled={isLocked}
                placeholder={section.hint}
              />
            ) : (
              <textarea
                className="input-base mt-2 min-h-[120px] resize-y"
                value={submission[section.key] ?? ''}
                onChange={(e) => handleChange(section.key, e.target.value)}
                maxLength={section.maxLength}
                disabled={isLocked}
                placeholder={section.hint}
              />
            )}
            <div className="mt-1 flex items-center justify-between">
              <div>
                {canRefine && section.type === 'textarea' && !isLocked && (
                  <button
                    className="inline-flex items-center gap-1 text-2xs font-medium text-purple-600 hover:text-purple-800 transition-colors"
                    onClick={() => handleRefine(section.key, submission[section.key] ?? '')}
                    disabled={refiningSection === section.key || (submission[section.key] ?? '').trim().length < 20}
                  >
                    {refiningSection === section.key ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Sparkles className="h-3 w-3" />
                    )}
                    {refiningSection === section.key ? 'Refining...' : 'AI Refine'}
                  </button>
                )}
              </div>
              <span className="text-2xs text-ink-tertiary">
                {(submission[section.key] ?? '').length}/{section.maxLength}
              </span>
            </div>

            {/* AI Refine Result */}
            {refineResult && refineResult.section === section.key && (
              <div className="mt-3 rounded-lg border border-purple-200 bg-purple-50 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-purple-800 flex items-center gap-1">
                    <Sparkles className="h-3.5 w-3.5" /> AI Suggestion
                  </span>
                  <button className="text-purple-400 hover:text-purple-600" onClick={() => setRefineResult(null)}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-purple-900">{refineResult.data.refined}</p>
                {refineResult.data.suggestions.length > 0 && (
                  <div className="mt-2">
                    <span className="text-2xs font-medium text-purple-700">Suggestions:</span>
                    <ul className="mt-1 list-disc pl-4 text-2xs text-purple-800 space-y-0.5">
                      {refineResult.data.suggestions.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <button
                  className="btn-primary mt-2 text-xs px-3 py-1"
                  onClick={applyRefinement}
                >
                  Apply
                </button>
              </div>
            )}
          </section>
        ))}

        {/* AI error (from inline refine) */}
        {aiError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
            {aiError}
          </div>
        )}

        {/* Links */}
        <section className="card p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-ink">Links & Resources</h3>
          <p className="mt-0.5 text-xs text-ink-tertiary">Share your demo video, repo, or any other resources</p>
          <div className="mt-3 space-y-3">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-ink-secondary">
                <Play className="h-3.5 w-3.5" /> Demo / Video URL
              </label>
              <input
                type="url"
                className="input-base mt-1"
                value={submission.demoUrl ?? ''}
                onChange={(e) => handleChange('demoUrl', e.target.value)}
                disabled={isLocked}
                placeholder="https://youtube.com/watch?v=... or Loom link"
              />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-ink-secondary">
                <GitBranch className="h-3.5 w-3.5" /> GitLab / GitHub Repository
              </label>
              <input
                type="url"
                className="input-base mt-1"
                value={submission.repoUrl ?? ''}
                onChange={(e) => handleChange('repoUrl', e.target.value)}
                disabled={isLocked}
                placeholder="https://gitlab.com/your-team/project"
              />
            </div>
          </div>
        </section>

        {/* File uploads */}
        <section className="card border-dashed p-4 sm:p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-ink">
            <Upload className="h-4 w-4" /> Attachments
          </div>
          <p className="mt-1 text-xs text-ink-tertiary">
            Upload mockups, slides, screenshots, or documents (PNG, JPEG, GIF, PDF, PPTX — max {MAX_ATTACHMENT_MB}MB, up to 5 files)
          </p>

          {/* Existing files */}
          {submission.fileUrls.length > 0 && (
            <div className="mt-3 space-y-2">
              {submission.fileUrls.map((url, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg bg-surface-sunken p-2">
                  {/\.(png|jpg|jpeg|gif|webp)$/i.test(url) ? (
                    <img src={url} alt="" className="h-10 w-10 rounded object-cover" />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200 text-xs font-medium text-gray-600">
                      {url.split('.').pop()?.toUpperCase() ?? 'FILE'}
                    </div>
                  )}
                  <a href={url} target="_blank" rel="noopener noreferrer" className="flex-1 text-sm text-ink hover:underline truncate">
                    {decodeURIComponent(url.split('/').pop() ?? `File ${i + 1}`)}
                  </a>
                  {!isLocked && (
                    <button
                      className="btn-ghost p-1 text-red-400 hover:text-red-600"
                      title="Remove file"
                      onClick={async () => {
                        const updated = submission.fileUrls.filter((_, idx) => idx !== i);
                        setSubmission({ ...submission, fileUrls: updated });
                        try {
                          await networkManager.put(
                            `/api/events/${eventId}/teams/${teamId}/submissions/${submission.submissionId}`,
                            { fileUrls: updated },
                          );
                        } catch (err) {
                          setError(getErrorMessage(err, 'Failed to remove file'));
                          setSubmission({ ...submission }); // revert
                        }
                      }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Upload button */}
          {!isLocked && submission.fileUrls.length < 5 && (
            <div className="mt-3">
              <input
                type="file"
                id="file-upload"
                className="hidden"
                accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.pptx"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > MAX_ATTACHMENT_BYTES) {
                    setError(`File must be under ${MAX_ATTACHMENT_MB}MB`);
                    return;
                  }
                  setError(null);
                  setUploading(true);
                  try {
                    // Get presigned URL
                    const presignRes = await networkManager.post<{ status: boolean; data: { uploadUrl: string; fileUrl: string } }>(
                      `/api/events/${eventId}/teams/${teamId}/submissions/${submission.submissionId}/upload`,
                      { fileName: file.name, contentType: file.type },
                    );
                    // Upload directly to S3. fetch() resolves on 4xx/5xx, so we
                    // must check response.ok explicitly — otherwise a silent
                    // rejection (403 signature mismatch, 413 oversize, etc.)
                    // gets committed to the submission with a broken URL.
                    const s3ContentType = file.type || 'application/octet-stream';
                    const s3Response = await fetch(presignRes.data.uploadUrl, {
                      method: 'PUT',
                      body: file,
                      headers: { 'Content-Type': s3ContentType },
                    });
                    if (!s3Response.ok) {
                      const body = await s3Response.text().catch(() => '');
                      // S3 returns XML; pull out <Code> for a short human-readable label.
                      const s3Code = body.match(/<Code>([^<]+)<\/Code>/)?.[1] ?? '';
                      throw new Error(
                        `S3 upload failed (${s3Response.status}${s3Code ? ` ${s3Code}` : ''}). ` +
                          `File: ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(1)}MB, type: ${s3ContentType}.`,
                      );
                    }
                    // Update submission with new file URL
                    setSubmission({
                      ...submission,
                      fileUrls: [...submission.fileUrls, presignRes.data.fileUrl],
                    });
                    // Save the fileUrl to backend
                    await networkManager.put(
                      `/api/events/${eventId}/teams/${teamId}/submissions/${submission.submissionId}`,
                      { fileUrls: [...submission.fileUrls, presignRes.data.fileUrl] },
                    );
                  } catch (err) {
                    setError(getErrorMessage(err, 'Failed to upload file'));
                  } finally {
                    setUploading(false);
                  }
                  e.target.value = '';
                }}
              />
              <label
                htmlFor="file-upload"
                className={`btn-secondary inline-flex cursor-pointer items-center gap-2 text-sm ${uploading ? 'pointer-events-none opacity-60' : ''}`}
              >
                {uploading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="h-4 w-4" /> Upload File</>
                )}
              </label>
            </div>
          )}
          {submission.fileUrls.length >= 5 && (
            <p className="mt-2 text-xs text-amber-600">Maximum 5 files reached</p>
          )}
        </section>
      </div>

      {/* Resources */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <BreakthroughTips tips={getTipsForContext('submission')} title="Thinking Tips" />
        <PersonaTips personas={PERSONAS.slice(0, 3)} title="Who are you solving for?" />
      </div>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-ring bg-surface-raised/95 px-4 py-3 backdrop-blur-sm safe-area-bottom">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <span className="text-sm text-ink-secondary">
            {isLocked ? (
              <span className="flex items-center gap-1.5"><Lock className="h-4 w-4" /> Finalized for demo day</span>
            ) : (
              <span>{filledSections}/{SECTIONS.length} sections · auto-saving</span>
            )}
          </span>
          <div className="flex gap-2">
            {isLocked ? (
              <button onClick={handleUnsubmit} className="btn-secondary">
                <Undo2 className="h-4 w-4" /> Unlock
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="btn-primary"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {progress < 100 ? 'Save' : 'Submit'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
